/**
 * A terminal strip of two to four labelled counts.
 *
 * Every cell carries its own label, because a row of bare figures is a row a
 * reader has to decode. The strip is terminal by design: it closes a card or a
 * band, and nothing follows it inside the same block.
 *
 * It WRAPS rather than shrinking its cells. Four labelled counts run past a
 * 160px card body — that label is the whole reason the figure is legible, and
 * shrinking cells is the one repair that takes it away. Wrapping keeps every
 * cell whole and costs a second line, which is the cheaper of the two.
 */
"use client";
import type { ReactElement } from "react";
import {
  CRC_NUMERIC_STYLE,
  CRC_STACK_GAP,
  CRC_TEXT_FAINT,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardAlign,
  CresultcardStackDensity,
  CresultcardTone,
  type CresultcardStatSpec,
} from "./cresultcard.types";

const STAT_GAP = 14;
/** Two rows of cells sit closer than two cells do: the line break is the gap. */
const STAT_ROW_GAP = CRC_STACK_GAP[CresultcardStackDensity.Snug];
const GLYPH_SIZE = 12;
const GLYPH_STROKE = 1.4;

interface CresultcardStatRowProps {
  readonly stats: readonly CresultcardStatSpec[];
  readonly align?: CresultcardAlign;
}

export function CresultcardStatRow({
  stats,
  align = CresultcardAlign.Start,
}: CresultcardStatRowProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/StatRow/Root"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        // Lines take their content height. `stretch` would hand a strip inside
        // a stretched holder its extra height and spread the cells down it.
        alignContent: "flex-start",
        justifyContent:
          align === CresultcardAlign.End ? "flex-end" : "flex-start",
        columnGap: STAT_GAP,
        rowGap: STAT_ROW_GAP,
        minWidth: 0,
      }}
    >
      {stats.map((stat) => {
        const Glyph = stat.glyph;
        return (
          <span
            data-el="Cresultcard/StatRow/Entry"
            key={stat.label}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: "0 0 auto",
              minWidth: 0,
            }}
          >
            <span
              style={{
                ...CRC_NUMERIC_STYLE,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                fontWeight: 740,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                color: CRC_TONE_COLOR[stat.tone ?? CresultcardTone.Neutral],
              }}
            >
              {Glyph != null && <Glyph size={GLYPH_SIZE} stroke={GLYPH_STROKE} />}
              {stat.value}
            </span>
            <span
              style={{
                fontSize: 8.5,
                fontWeight: 620,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                lineHeight: 1,
                color: CRC_TEXT_FAINT,
              }}
            >
              {stat.label}
            </span>
          </span>
        );
      })}
    </div>
  );
}
